import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RegisterForm from './RegisterForm';
import './LoginForm.css';

// Force LTR on login page
const useForceLTR = () => {
  useEffect(() => {
    const html = document.documentElement;
    const originalDir = html.getAttribute('dir');
    html.setAttribute('dir', 'ltr');
    return () => {
      if (originalDir) html.setAttribute('dir', originalDir);
    };
  }, []);
};

const LoginForm = ({ onLogin, onRegister, currentLanguage, setCurrentLanguage, languages, t }) => {
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState('');

  // Apply LTR for login page
  useForceLTR();

  // Validation
  const validateUsername = (value) => {
    if (!value.trim()) {
      return t('usernameRequired', 'Username is required');
    }
    return '';
  };

  const validatePassword = (value) => {
    if (!value) {
      return t('passwordRequired', 'Password is required');
    }
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'username':
        return validateUsername(value);
      case 'password':
        return validatePassword(value);
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setServerError('');

    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({ username: true, password: true });

    // Validate all fields
    const usernameError = validateUsername(formData.username);
    const passwordError = validatePassword(formData.password);

    const newErrors = {};
    if (usernameError) newErrors.username = usernameError;
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    setServerError('');

    try {
      const result = await onLogin(formData);

      if (result && !result.success) {
        // Handle specific error types
        if (result.message) {
          setServerError(result.message);
        } else if (result.error === 'invalid_credentials') {
          setServerError(t('invalidCredentials', 'Invalid username or password'));
        } else if (result.error === 'user_not_found') {
          setServerError(t('userNotFound', 'User not found. Please check your username.'));
        } else if (result.error === 'wrong_password') {
          setServerError(t('wrongPassword', 'Incorrect password. Please try again.'));
        } else if (result.error === 'account_disabled') {
          setServerError(t('accountDisabled', 'Your account has been disabled. Contact support.'));
        } else {
          setServerError(t('loginFailed', 'Login failed. Please try again.'));
        }
      }
    } catch (error) {
      setServerError(error.message || t('loginFailed', 'Login failed. Please try again.'));
    }

    setLoading(false);
  };

  const handleRegisterSuccess = (result) => {
    if (result && result.success) {
      setShowRegister(false);
      setServerError('');
    }
  };

  const getInputClass = (fieldName) => {
    let className = 'login-input';
    if (touched[fieldName] && errors[fieldName]) {
      className += ' input-error';
    }
    return className;
  };

  if (showRegister) {
    return (
      <RegisterForm
        onRegister={onRegister}
        onBack={() => setShowRegister(false)}
        onSuccess={handleRegisterSuccess}
        t={t}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-wrapper">
        <div className="login-header">
          <h2 className="login-title">
            {t('title', 'COD Tournament')}
          </h2>
          <p className="login-subtitle">
            {t('loginSubtitle', 'Sign in to your account')}
          </p>

          <div className="language-selector">
            <select
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              className="language-select"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.nativeName || lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {serverError && (
          <div className="server-error">
            <span className="error-icon">⚠️</span>
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {/* Username */}
          <div className="input-group">
            <label className="input-label">{t('username', 'Username')}</label>
            <input dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="text"
              name="username"
              placeholder={t('enterUsername', 'Enter your username')}
              value={formData.username}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('username')}
              disabled={loading}
              autoComplete="username"
            />
            {touched.username && errors.username && (
              <span className="field-error">{errors.username}</span>
            )}
          </div>

          {/* Password */}
          <div className="input-group">
            <label className="input-label">{t('password', 'Password')}</label>
            <input dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="password"
              name="password"
              placeholder={t('enterPassword', 'Enter your password')}
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('password')}
              disabled={loading}
              autoComplete="current-password"
            />
            {touched.password && errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </div>

          <div className="forgot-password-link-wrapper">
            <Link to="/forgot-password" className="forgot-password-link">
              {t('forgotPassword', 'Forgot password?')}
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="login-button"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {t('loggingIn', 'Logging in...')}
              </>
            ) : (
              t('login', 'Login')
            )}
          </button>

          <p className="register-link-text">
            {t('dontHaveAccount', "Don't have an account?")}{' '}
            <button
              type="button"
              onClick={() => setShowRegister(true)}
              className="register-link"
            >
              {t('register', 'Register')}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
